import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { key: "revenue",     label: "Revenue report",          icon: FileSpreadsheet },
  { key: "subscriptions", label: "Subscription report",    icon: FileSpreadsheet },
  { key: "org_growth",  label: "Organization growth",     icon: FileText },
  { key: "user_growth", label: "User growth",             icon: FileText },
  { key: "marketplace", label: "Marketplace report",      icon: FileSpreadsheet },
  { key: "ai_usage",    label: "AI usage report",         icon: FileText },
  { key: "api_usage",   label: "API usage report",        icon: FileText },
];

function toCSV(rows) {
  if (!rows.length) return "";
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r))));
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

export default function Reports() {
  const [loading, setLoading] = useState(null);

  const download = async (kind, format) => {
    setLoading(kind + format);
    try {
      const { data } = await api.get(`/admin/reports/${kind}`);
      const rows = Array.isArray(data) ? data : [];
      let blob, filename;
      if (format === "csv") { blob = new Blob([toCSV(rows)], { type: "text/csv" }); filename = `${kind}.csv`; }
      else { blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" }); filename = `${kind}.json`; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(null); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="admin-reports">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-black">Reports & analytics</h1>
        <p className="text-muted-foreground">Download platform reports as CSV or JSON. PDF/Excel exports coming next iteration.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <Card key={r.key} data-testid={`report-${r.key}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center gap-2">
                <r.icon className="w-5 h-5 text-primary" />
                <div className="font-semibold">{r.label}</div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => download(r.key, "csv")} disabled={loading === r.key + "csv"} data-testid={`report-${r.key}-csv`}>
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
                <Button size="sm" variant="outline" onClick={() => download(r.key, "json")} disabled={loading === r.key + "json"} data-testid={`report-${r.key}-json`}>
                  <Download className="w-4 h-4 mr-1" /> JSON
                </Button>
                <Badge variant="outline" className="text-[10px]">PDF · soon</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
